import dayjs from "dayjs";

/**
 * deltaHistory.js — 델타전략 히스토리에서 BTC 적립분을 빼는 규칙 (DAS-179).
 *
 * ## 왜 과거는 뺄 수 없나
 *
 * `balance` 테이블은 전략별 `fiat_balance` · `risky_balance` **총액만** 저장한다 — 코인별 내역이 없다.
 * 보유 BTC 는 그 fiat 총액 안에 섞여 있고, 과거 시점의 BTC 평가액을 알 방법이 없다.
 * 업비트 과거 시세는 구할 수 있어도 **그때 몇 개를 갖고 있었는지**가 없어 소급이 불가능하다.
 *
 * DAS-177 의 `btc_snapshot` 이 그 값을 남기기 시작했으므로 **스냅샷이 있는 날만** 뺄 수 있다.
 *
 * ## 점 하나가 들고 다니는 세 값 (DAS-180)
 *
 * - `totalRaw` — 코인 총잔고(BTC 포함). **항상** 있다.
 * - `delta`    — BTC 를 뺀 값. 스냅샷이 없는 날은 `null`.
 * - `total`    — 표·요약이 쓰는 "가능하면 뺀" 값 = `delta ?? totalRaw`.
 *
 * 차트가 `totalRaw` 와 `delta` 를 **각자 다른 계열로** 그리기 때문에 이렇게 나눠 둔다.
 * 한 계열이 중간에 의미를 갈아타면 경계에서 BTC 금액만큼의 낙하가 선으로 그려져,
 * 기준이 바뀐 것을 실제 손실처럼 보이게 한다 (DAS-180 에서 고친 버그).
 */

const arr = (v) => (Array.isArray(v) ? v : []);

/**
 * btc series 응답(points)을 날짜 → 평가액 맵으로 만든다.
 * @param {Array<{date: string, value_krw: number}>} points
 * @returns {Map<string, number>} "YYYY-MM-DD" → 평가액(KRW)
 */
export function buildBtcValueByDate(points) {
    const map = new Map();
    arr(points).forEach((p) => {
        if (!p || !p.date) return;
        const v = Number(p.value_krw);
        if (Number.isFinite(v)) map.set(String(p.date), v);
    });
    return map;
}

/**
 * 전략별 배열의 배열을 **같은 시각끼리 합산**해 한 줄기로 만든다 (DAS-180).
 *
 * 잔고 이력은 전략마다 따로 오고 시각이 거의 겹친다. 합치지 않고 그냥 이어 붙이면
 * DeltaFx 행(₩1억대)과 잔고가 비어 있는 전략 행(수천 원)이 번갈아 찍혀 톱니가 된다.
 * History 의 총잔고 차트가 쓰는 방식(시간 단위 합산)과 같게 맞춘다 — 델타 Hero 도
 * 전략 합산에서 BTC 를 빼는 값이라, 합산해야 Hero 와 차트가 같은 값이 된다.
 *
 * @param {Array} groups 전략별 배열의 배열 (또는 평평한 배열)
 * @param {string[]} excludeStrategies 합산에서 뺄 전략명
 */
export function sumByHour(groups, excludeStrategies = []) {
    const byHour = new Map();
    arr(groups)
        .flat()
        .filter((row) => row && row.created && !excludeStrategies.includes(row.strategy_name || "default"))
        .forEach((row) => {
            const hour = dayjs(row.created).startOf("hour");
            const key = hour.valueOf();
            const cur = byHour.get(key) || { time: key, date: hour.format("YYYY-MM-DD"), total: 0, fiat: 0, risky: 0, rate: 0 };
            cur.total += Number(row.total_balance) || 0;
            cur.fiat += Number(row.fiat_balance) || 0;
            cur.risky += Number(row.risky_balance_krw) || 0;
            // 환율은 합산 대상이 아니다 — 그 시각의 값 하나를 그대로 쓴다.
            const rate = Number(row.exchange_rate) || 0;
            if (rate > 0) cur.rate = rate;
            byHour.set(key, cur);
        });
    return Array.from(byHour.values()).sort((a, b) => a.time - b.time);
}

/**
 * 합산된 시간별 잔고에서 BTC 적립분을 뺀 시리즈를 만든다.
 *
 * 히스토리는 시간별이고 스냅샷은 일별이므로 **같은 날짜(KST 기준 YYYY-MM-DD)** 로 맞춘다.
 * 그날 스냅샷이 없으면 `delta` 를 `null` 로 둔다 — 값을 지어내지 않는다.
 *
 * @param {Array} balanceHistory 전략별 배열의 배열 (또는 평평한 배열)
 * @param {Array} btcPoints      `/api/btc/series` 의 points
 * @param {Object} [options]
 * @param {string[]} [options.excludeStrategies] 합산에서 뺄 전략명
 * @returns {{points: Array, boundaryTime: number|null, hasIncludedSegment: boolean, snapshotSince: string|null}}
 */
export function buildDeltaSeries(balanceHistory, btcPoints, options = {}) {
    const { excludeStrategies = [] } = options;
    const btcByDate = buildBtcValueByDate(btcPoints);

    const points = sumByHour(balanceHistory, excludeStrategies).map((h) => {
        const hasSnap = btcByDate.has(h.date);
        const btcValue = hasSnap ? btcByDate.get(h.date) : 0;
        // 스냅샷과 잔고 조회 시점이 어긋나면 순간적으로 BTC 가 더 클 수 있어 0에서 막는다.
        const delta = hasSnap ? Math.max(0, h.total - btcValue) : null;
        return {
            time: h.time,
            date: h.date,
            totalRaw: h.total,
            delta,
            total: delta !== null ? delta : h.total,
            fiat: hasSnap ? Math.max(0, h.fiat - btcValue) : h.fiat,
            risky: h.risky,
            rate: h.rate,
            btcValue,
            btcExcluded: hasSnap,
        };
    });

    const firstExcluded = points.findIndex((p) => p.btcExcluded);
    return {
        points,
        boundaryTime: firstExcluded >= 0 ? points[firstExcluded].time : null,
        hasIncludedSegment: points.some((p) => !p.btcExcluded),
        snapshotSince: firstExcluded >= 0 ? points[firstExcluded].date : null,
    };
}

/**
 * 델타 시리즈에서 월별 손익 요약을 만든다.
 *
 * 시작 잔고는 **직전 달의 마감 잔고**를 쓴다 (History 의 기존 규칙과 동일 — 달과 달 사이가
 * 끊기지 않게 한다). 첫 달만 자기 달의 첫 값을 시작으로 삼는다.
 *
 * 한 달 안에 BTC 를 빼지 못한 점이 하나라도 있으면 그 달은 `btcIncluded: true` 다 —
 * 손익금이 BTC 등락에 오염됐다는 뜻이므로 화면이 표식을 달아야 한다.
 *
 * @returns {Array<{month, startTotal, endTotal, profitLoss, profitLossRate, btcIncluded}>} 최신 달 우선
 */
export function buildDeltaMonthlySummary(points) {
    const byMonth = new Map();
    arr(points).forEach((p) => {
        if (!p || !p.date) return;
        const month = p.date.slice(0, 7);
        const cur = byMonth.get(month);
        if (!cur) byMonth.set(month, { first: p, end: p, anyIncluded: !p.btcExcluded });
        else byMonth.set(month, { first: cur.first, end: p, anyIncluded: cur.anyIncluded || !p.btcExcluded });
    });

    const months = Array.from(byMonth.keys()).sort();
    let prevEnd = null;
    const rows = months.map((month) => {
        const { first, end, anyIncluded } = byMonth.get(month);
        const endTotal = end.total;
        const startTotal = prevEnd !== null ? prevEnd : first.total;
        const profitLoss = endTotal - startTotal;
        prevEnd = endTotal;
        return {
            month,
            startTotal,
            endTotal,
            profitLoss,
            profitLossRate: startTotal !== 0 ? (profitLoss / startTotal) * 100 : 0,
            btcIncluded: anyIncluded,
        };
    });
    return rows.reverse();
}
